import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReportFlow from "../../components/ReportFlow/ReportFlow";
import NotesBoard from "../../components/NotesBoard/NotesBoard";
import { logActivity, logActivityBeacon } from "../../utils/activity";
import HUDHeader from "../../components/HUDHeader/HUDHeader";
import CameraStreams from "../../components/CameraStreams/CameraStreams";
import ChatInterface from "../../components/ChatInterface/ChatInterface";
import GameControl from "../../components/GameControl/GameControl";
import Title from "../../components/Title/Title";
import { GameDetailConfigProvider } from "../../context/GameDetailConfig";
import { WebSocketProvider } from "../../context/WebSocketContext";
import "./HUD.css";
import Topbar from "../../components/Topbar/Topbar";
import {useGameConfig} from "../../context/GameConfig";
import GameControlPassenger from "../../components/GameControlPassenger/GameControlPassenger";
import GameControlPortalMaster from "../../components/GameControlPortalMaster/GameControlPortalMaster";
import GameControlSpymaster from "../../components/GameControlSpymaster/GameControlSpymaster";
import { HintProvider } from "../../hints/HintContext";

const HUD = () => {
    const { gameId } = useParams();
    const navigate = useNavigate();
    const {roomConfig} = useGameConfig();
    // Report ("Afslut spil") opens inline in the right column, so the GM never leaves the camera view.
    const [showReport, setShowReport] = useState(false);

    // didMount skips StrictMode's intentional first unmount (dev only)
    const didMount = useRef(false);
    const openedAt = useRef(Date.now());
    const isUnloading = useRef(false);

    useEffect(() => {
        openedAt.current = Date.now();
        isUnloading.current = false;
        let sent = false;

        const getDuration = () => {
            const s = Math.floor((Date.now() - openedAt.current) / 1000);
            const h = Math.floor(s / 3600);
            const m = Math.floor((s % 3600) / 60);
            const sec = s % 60;
            if (h > 0) return `${h}t ${m}m ${sec}s`;
            if (m > 0) return `${m}m ${sec}s`;
            return `${sec}s`;
        };

        const sendClose = () => {
            if (sent) return;
            sent = true;
            const details = `${gameId} — åbent i ${getDuration()}`;
            if (isUnloading.current) {
                logActivityBeacon("close_room", details);
            } else {
                logActivity("close_room", details);
            }
        };

        const onUnload = () => { isUnloading.current = true; sendClose(); };
        window.addEventListener("beforeunload", onUnload);
        return () => {
            window.removeEventListener("beforeunload", onUnload);
            if (didMount.current) sendClose();
            didMount.current = true;
        };
    }, [gameId]);

    const userEmail = localStorage.getItem("userEmail") || "";

    const handleLogout = () => {
        logActivity("logout");
        localStorage.removeItem("accessToken");
        localStorage.removeItem("userEmail");
        navigate("/");
    };

    // The right column: the shared GM notes board pinned on top (outside the scroll area, so it is
    // always in view), then the game control, with the "Afslut spil" report shown in the same
    // scroll area when open. The control is hidden (display:none)
    // rather than unmounted so its WebSocket stays connected while the GM fills the report; the
    // notes board and the chat below stay visible throughout.
    // The hint panel (SCRUM-686) lives inside the chat bubble: it reads the flow and game clock
    // from the WebSocket context and hands the picked hint to the chat box, so the chat sits
    // inside the HintProvider, which sits inside the WebSocketProvider.
    const controlColumn = (controlNode, withChat = true) => (
        <div className="game-control">
            <WebSocketProvider gameId={gameId}>
                <HintProvider>
                    <NotesBoard />
                    <div className="game-control-scroll">
                        <div style={showReport ? { display: "none" } : undefined}>{controlNode}</div>
                        {showReport && <ReportFlow onClose={() => setShowReport(false)} />}
                    </div>
                    {withChat && <ChatInterface gameId={gameId} />}
                </HintProvider>
            </WebSocketProvider>
        </div>
    );

    return (
        <GameDetailConfigProvider gameId={gameId}>
            <Topbar
                logo={<Title gameId={gameId} />}
                userEmail={userEmail}
                onLogout={handleLogout}
                // The 'indgang' view has no control column to host the inline report, so route to
                // the standalone /afslut-spil page instead of opening it inline into nothing.
                onEndGame={gameId === "indgang" ? undefined : () => setShowReport(true)}
            />
            <div className="hud-page">
                <div className="content">
                        {(() => {
                            switch (gameId) {
                                case 'nighttrain':
                                    return (
                                        <>
                                            <div className="camera-streams">
                                                <CameraStreams gameId={gameId} roomConfig={roomConfig}/>
                                            </div>
                                            {controlColumn(
                                                <>
                                                    <HUDHeader hasStopButton={true} />
                                                    <GameControlPassenger/>
                                                </>
                                            )}
                                        </>
                                    );
                                case 'urbex':
                                    return (
                                        <>
                                            <div className="camera-streams">
                                                <CameraStreams gameId={gameId} roomConfig={roomConfig}/>
                                            </div>
                                            {controlColumn(
                                                <>
                                                    <HUDHeader />
                                                    <GameControl/>
                                                </>
                                            )}
                                        </>
                                    );
                                case 'multiportal':
                                    return (
                                        <>
                                            {controlColumn(
                                                <GameControlPortalMaster/>,
                                                false
                                            )}
                                        </>
                                    );
                                case 'indgang':
                                    return (
                                        <>
                                            <div className="camera-streams">
                                                <CameraStreams gameId={gameId} roomConfig={roomConfig}/>
                                            </div>
                                        </>
                                    );
                                case 'spymaster':
                                case 'spymaster1':
                                    return (
                                        <>
                                            <div className="camera-streams">
                                                <CameraStreams gameId={gameId} roomConfig={roomConfig}/>
                                            </div>
                                            {controlColumn(
                                                <>
                                                    <HUDHeader />
                                                    <GameControlSpymaster/>
                                                </>
                                            )}
                                        </>
                                    );
                                default:
                                    return (
                                        <>
                                            <div className="camera-streams">
                                                <CameraStreams gameId={gameId} roomConfig={roomConfig}/>
                                            </div>
                                            {controlColumn(
                                                <>
                                                    <HUDHeader  />
                                                </>
                                            )}
                                        </>
                                    );
                            }
                        })()}
                </div>
            </div>
        </GameDetailConfigProvider>

    );
};

export default HUD;